import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { AuthError, defaultLandingFor, requestOtp, verifyOtp, type UserRole } from "@/lib/auth";
import { supabase } from "@/lib/supabase";
import { claimLegacyMeetupParticipations } from "@/lib/api/participants";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Field } from "@/components/ui/Field";
import { Input } from "@/components/ui/Input";
import { Logo } from "@/components/ui/Logo";
import { ErrorBlock, InlineAlert } from "@/components/ui/States";
import { PublicLayout } from "@/components/layout/PublicLayout";

/**
 * Second step of the global passwordless flow: enter the 6-digit code
 * sent to the email collected on /sign-in.
 *
 * On success:
 *   1. supabase.auth.verifyOtp establishes the session.
 *   2. claimLegacyMeetupParticipations() back-fills any pre-existing
 *      anonymous event participations that share this email.
 *   3. We look up users.active_role to pick a sensible landing route,
 *      honoring `?next=` if provided.
 */
export function VerifyOtpPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const email = (params.get("email") ?? "").toLowerCase();
  const next = params.get("next");

  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resendNotice, setResendNotice] = useState<string | null>(null);
  const [resending, setResending] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError("Email is missing. Go back and enter it again.");
      return;
    }
    const trimmed = code.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setError("Enter the 6-digit code from the email.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const { user } = await verifyOtp(email, trimmed);

      try {
        await claimLegacyMeetupParticipations();
      } catch {
        // Non-fatal — never block sign-in on a silent claim failure.
      }

      let target = next;
      if (!target && user) {
        const { data } = await supabase
          .from("users")
          .select("active_role")
          .eq("id", user.id)
          .maybeSingle();
        target = defaultLandingFor((data?.active_role as UserRole | null) ?? null);
      }
      navigate(target ?? "/me/meetups", { replace: true });
    } catch (err) {
      setError(err instanceof AuthError ? err.message : "Invalid code. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const onResend = async () => {
    if (!email) return;
    setError(null);
    setResendNotice(null);
    setResending(true);
    try {
      await requestOtp(email);
      setResendNotice("Code sent again.");
    } catch (err) {
      setError(
        err instanceof AuthError ? err.message : "Couldn't send the code. Try again.",
      );
    } finally {
      setResending(false);
    }
  };

  return (
    <PublicLayout>
      <div className="w-full max-w-[440px] animate-fade-in">
        <Logo size="md" showTagline className="mb-8" />

        <Card padding="lg">
          <h2 className="text-[28px] font-bold tracking-tight text-[#1a1a1a]">
            Enter your code
          </h2>
          <p className="mt-2 break-words text-[15px] text-[#6c757d]">
            {email ? <>We sent a 6-digit code to <strong>{email}</strong></> : "Check your inbox for the 6-digit code."}
          </p>

          <form className="mt-6 flex flex-col gap-5" onSubmit={onSubmit}>
            {error ? <ErrorBlock title="Verification failed" message={error} /> : null}
            {resendNotice ? <InlineAlert tone="success">{resendNotice}</InlineAlert> : null}

            <Field label="Code from the email" required>
              <Input
                type="text"
                required
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                autoComplete="one-time-code"
                autoFocus
              />
            </Field>

            <Button type="submit" loading={submitting} fullWidth disabled={code.length !== 6}>
              {submitting ? "Verifying..." : "Confirm"}
            </Button>
          </form>

          <button
            type="button"
            onClick={onResend}
            disabled={resending || !email}
            className="mt-4 w-full text-center text-[13px] font-semibold text-[#2563eb] hover:text-[#1d4ed8] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {resending ? "Sending..." : "Didn't get the email? Send again"}
          </button>

          <p className="mt-4 text-center text-[13px] text-[#6c757d]">
            Wrong address?{" "}
            <Link to="/sign-in" className="font-semibold text-[#2563eb] hover:text-[#1d4ed8]">
              Use a different email
            </Link>
          </p>
        </Card>
      </div>
    </PublicLayout>
  );
}
